import { useState } from "react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { NumberFormatValues } from "react-number-format"
import { appStakeUnstakePopupState } from "@/atoms/appPopupState"
import { modalAmountAtom } from "@/atoms/modal"
import { restakingAccountAtom, restakingBalancesAtom, restakingVaultState } from "@/atoms/restakingState"
import { networkAtom, refreshRestakingAtom } from "@/atoms/settings"
import { MODAL_ABORT_CONTROLLER_MESSAGE, MODAL_ABORT_CONTROLLER_TIMEOUT } from "@/components/dashboard/modal/constants"
import { ModalTxResult } from "@/components/modalTxResult"
import { RelendModal } from "@/components/relendModal"
import { StakingSelectItem } from "@/components/staking/components/detail/types"
import { DAYS_IN_YEAR, RESTAKING_MINT, STAKING, UNSTAKING } from "@/utils/constants"
import { HelperText, TransactionDetail } from "@/utils/dataTypes"
import { stringifyVariable } from "@/utils/helpers"
import { Stack, Typography } from "@mui/material"
import { useRollbar } from "@rollbar/react"
import { Keypair } from "@solana/web3.js"
import {
  DEFAULT_HELPER_TEXT,
  DEFAULT_TRANSACTION_DETAIL,
  handleRestakingError,
  handleRestakingSuccessful,
  verifyStakeUnstakeAction,
} from "../../shared/commons"
import { StakingInputForm } from "../inputForm"
import useRestaking, { RestakingResult } from "@/hooks/useRestaking"
import useTrans from "@/hooks/useTrans"
import Button from "@/components/base/Button"
import StakeUnstakeModalStat from "./StakeUnstakeModalStat"
import * as styles from "./styles"

interface IViewProps {
  isShowForm?: boolean
}

const View = ({ isShowForm = true }: IViewProps) => {
  const trans = useTrans()
  const [loading, setLoading] = useState<boolean | undefined>(false)
  const [helperText, setHelperText] = useState<HelperText>({ ...DEFAULT_HELPER_TEXT })
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({ ...DEFAULT_TRANSACTION_DETAIL })
  const {
    address,
    stakingFn,
    unstakingNWithdrawFn,
    stakingItemInfo,
    rewardItemInfo,
    stakedAmountUI,
    currentStakingAPY,
    currentTotalStakedAmount,
  } = useRestaking()
  const rollbar = useRollbar()

  // atoms
  const [popupState] = useAtom(appStakeUnstakePopupState)
  const [amount, setAmount] = useAtom(modalAmountAtom)
  const restakingVault = useAtomValue(restakingVaultState)
  const restakingAccount = useAtomValue(restakingAccountAtom)
  const balances = useAtomValue(restakingBalancesAtom)
  const refreshStakingData = useSetAtom(refreshRestakingAtom)

  // for switching network
  const network = useAtomValue(networkAtom)

  const newTotalStakers = restakingVault
    ? restakingAccount
      ? restakingVault.vaultStat.totalStakers // If the user is already staking, the total stakers should not be increased
      : restakingVault.vaultStat.totalStakers + 1 // If the user is not staking, the total stakers should be increased by 1
    : 0
  const newUserYearlyReward = restakingVault
    ? (restakingVault.vaultStat.emissionPerDay * DAYS_IN_YEAR) / newTotalStakers
    : 0
  const newAPY =
    restakingVault && amount && amount !== 0
      ? newUserYearlyReward / (currentTotalStakedAmount + amount)
      : currentStakingAPY

  const selectedStakingItem: StakingSelectItem = {
    value: RESTAKING_MINT,
    icon: stakingItemInfo?.logoURI ?? "https://picsum.photos/200",
    iconName: stakingItemInfo?.name ?? "RANDOM_ICON_NAME",
    alt: stakingItemInfo?.symbol ?? "RANDOM_ALT",
    symbol: stakingItemInfo?.symbol ?? "RANDOM_SYMBOL",
    decimals: stakingItemInfo?.decimals ?? 9,
    rewardTokenMint: restakingVault?.rewards[0].rewardTokenMint ?? Keypair.generate().publicKey,
    apy: newAPY,
    timestamp: restakingVault?.rewards[0].timestamp ?? Math.floor(Date.now() / 1000),
    rewardTokenSymbol: rewardItemInfo?.symbol ?? "RANDOM_SYMBOL",
    rewardTokenName: rewardItemInfo?.name ?? "RANDOM_ICON_NAME",
    rewardTokenIcon: rewardItemInfo?.logoURI ?? "RANDOM_ICON",
  }

  const isStaking = popupState.type === STAKING

  const handleAmountChange = (values: NumberFormatValues) => {
    setAmount(values.floatValue)
    setHelperText({ content: "", color: "" })
  }

  // abort controller to control open and close model actions
  const [abortController, setAbortController] = useState(new AbortController())

  const handleCloseModal = () => {
    if (!loading) {
      // Abort the current operation when closing the modal
      abortAndSetNewAbortController()
      setAmount(0)
      setHelperText({ ...DEFAULT_HELPER_TEXT })
      setTransactionDetail({ ...DEFAULT_TRANSACTION_DETAIL })
    }
  }

  const abortAndSetNewAbortController = () => {
    abortController.abort(MODAL_ABORT_CONTROLLER_MESSAGE)
    // Timeout so that the code inside the function called this method can be executed
    setTimeout(() => {
      setAbortController(new AbortController())
    }, MODAL_ABORT_CONTROLLER_TIMEOUT)
  }

  const actionHandler = async () => {
    if (!amount) {
      return
    }

    try {
      setLoading(true)
      let actionFn: (amount: number, decimals: number) => Promise<RestakingResult>
      let type: typeof STAKING | typeof UNSTAKING
      if (isStaking) {
        actionFn = stakingFn
        type = STAKING
      } else {
        actionFn = unstakingNWithdrawFn
        type = UNSTAKING
      }

      const actionResult = await actionFn(amount, selectedStakingItem.decimals)
      if (!actionResult.isSucceeded) {
        handleRestakingError(
          amount,
          selectedStakingItem.symbol,
          restakingAccount,
          actionResult,
          setHelperText,
          setTransactionDetail,
          rollbar,
          address,
          type,
          trans,
        )
        return
      }

      await handleRestakingSuccessful(
        network,
        amount,
        selectedStakingItem.symbol,
        actionResult,
        setTransactionDetail,
        address,
        type,
        trans,
        refreshStakingData,
      )
    } catch (e: any) {
      setHelperText({ content: isStaking ? trans.staking.errors.stake : trans.staking.errors.unstake, color: "red" })
      rollbar.error(
        `RELEND: Error when claiming staking token. Error: Wallet address: ${address}. ${stringifyVariable(e)}`,
      )
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const buttonError = verifyStakeUnstakeAction(amount, balances.stakeToken, stakedAmountUI, popupState.type, trans)

  const submitButtonContent = buttonError
    ? buttonError
    : isStaking
      ? trans.staking.modal.stake
      : trans.staking.detail.start_unstake

  return (
    <>
      {isShowForm && (
        <Stack gap={2}>
          <StakingInputForm
            value={amount}
            setValue={setAmount}
            onValueChange={handleAmountChange}
            selectedStakingItem={selectedStakingItem}
            tokenBalance={balances.stakeToken}
            type={popupState.type}
            disabled={loading}
          />
          <StakeUnstakeModalStat amount={amount} selectedStakingItem={selectedStakingItem} />
          {helperText.content && (
            <Typography sx={{ ...styles.helperText, color: helperText.color }}>{helperText.content}</Typography>
          )}
          <Button
            variant={isStaking ? "primary" : "outlined"}
            size='lg'
            disabled={!!buttonError}
            loading={loading}
            onClick={actionHandler}
            data-testid='restaking-modal-stake-unstake-button'
          >
            {submitButtonContent}
          </Button>
        </Stack>
      )}

      {transactionDetail.isActive && (
        <RelendModal
          open={transactionDetail.isActive}
          handleClose={handleCloseModal}
          data-testid='modal-stake-unstake'
          title={isStaking ? trans.staking.modal.staking : trans.staking.modal.unstaking}
          showCloseButton
          hideButtonClose
          isSmallPopup
          useDrawerOnMobile
        >
          <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseModal} />
        </RelendModal>
      )}
    </>
  )
}

export default View
